import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  Alert,
  IconButton,
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import PageTitle from '../../../components/PageTitle';
import Stage0_Signup, { validateSignup } from '../pricing-wizard/stages/Stage0_Signup';
import { useCreateMerchantMutation } from '../../../redux/wizardApiSlice';
import { setStage } from '../../../redux/pricingWizardSlice';

// KYC documents required during initial onboarding. Backend will replace this
// list / wire the actual upload API. This is the canonical UI placeholder so
// the flow is testable end-to-end.
const KYC_DOCS = [
  { id: 'pan',     label: 'PAN Card',                hint: 'Business PAN (PDF / image, max 5 MB)' },
  { id: 'gst',     label: 'GST Certificate',         hint: 'Valid GST registration certificate' },
  { id: 'cheque',  label: 'Cancelled Cheque',        hint: 'Cancelled cheque from the registered bank account' },
  { id: 'address', label: 'Registered Address Proof', hint: 'Utility bill / lease deed / property tax receipt' },
];

export default function Step1_SetupKYC() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const signup = useSelector((s) => s.pricingWizard.signup);
  const merchant = useSelector((s) => s.pricingWizard.merchant);

  const [signupErrors, setSignupErrors] = useState({});
  const [createMerchant] = useCreateMerchantMutation();
  // Local placeholder state: { [docId]: File | null }
  const [docs, setDocs] = useState({});

  const handlePickFile = (docId) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocs((prev) => ({ ...prev, [docId]: file }));
  };

  const handleClearFile = (docId) => () => {
    setDocs((prev) => ({ ...prev, [docId]: null }));
  };

  const uploadedCount = KYC_DOCS.filter((d) => docs[d.id]).length;
  const allDocsUploaded = uploadedCount === KYC_DOCS.length;

  const handleSaveAndContinue = async () => {
    const errs = validateSignup(signup);
    setSignupErrors(errs);
    if (Object.keys(errs).length > 0) {
      enqueueSnackbar('Please fix the highlighted signup fields before continuing.', { variant: 'error' });
      return;
    }
    if (!allDocsUploaded) {
      enqueueSnackbar(
        `Upload all ${KYC_DOCS.length} KYC documents to continue (${uploadedCount}/${KYC_DOCS.length} done).`,
        { variant: 'warning' },
      );
      return;
    }

    // Log fields collected on this step but not sent to the create merchant API
    console.log('[Step 1] Form data NOT consumed by merchant/create API:', {
      brand: signup.brand,                          // used as name fallback only
      tnc: signup.tnc,                              // T&C acceptance
      kycDocs: Object.fromEntries(                  // local KYC file objects (not uploaded yet)
        Object.entries(docs).map(([k, v]) => [k, v ? { name: v.name, size: v.size, type: v.type } : null])
      ),
    });

    // Create merchant via RTK mutation (sets merchantId + merchant in Redux).
    if (!merchant) {
      try {
        await createMerchant(signup).unwrap();
      } catch (err) {
        enqueueSnackbar(err?.data?.message || err?.message || 'Failed to create merchant — please retry', { variant: 'error' });
        return;
      }
    }
    // Skip Stage 0 (Signup) in the pricing wizard — signup is already done here.
    dispatch(setStage(1));
    enqueueSnackbar('Step 1 complete — continuing to Step 2 (Pricing Setup).', { variant: 'success' });
    navigate('/merchant-config/pricing-setup');
  };

  return (
    <>
      <PageTitle>Step 1 · Initial Setup & KYC</PageTitle>
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: 2.5, py: 2 }}>
      
        {/* ── Flow context strip ── */}
        <Box
          sx={{
            mb: 2.5,
            p: 1.75,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.8px' }}>
                Merchant Onboarding · Step 1 of 4
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>
                Initial Setup & KYC
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Capture the merchant&apos;s basic details and upload KYC documents. Once complete, the next steps —
                Pricing, TAT, Allocation — are unlocked end-to-end.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip size="small" label="Step 1 · Setup & KYC" color="primary" sx={{ fontWeight: 600 }} />
              <Chip size="small" label="Step 2 · Pricing" variant="outlined" />
              <Chip size="small" label="Step 3 · TAT" variant="outlined" />
              <Chip size="small" label="Step 4 · Allocation" variant="outlined" />
            </Stack>
          </Stack>
        </Box>

        {/* ── Section A: Merchant Signup (reuses Stage0_Signup — does NOT modify the source) ── */}
        <Card sx={{ p: 2.5, mb: 2.5, boxShadow: (t) => t.customShadows?.z1 || 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Section A · Merchant Sign-up
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Same fields that previously lived inside the Pricing Wizard&apos;s Stage 0. The Pricing Wizard
            continues to render this form when entered standalone — no flows have been broken.
          </Typography>
          <Stage0_Signup errors={signupErrors} />
        </Card>

        {/* ── Section B: KYC Documents Upload (placeholder UI — backend wiring pending) ── */}
        <Card sx={{ p: 2.5, mb: 2.5, boxShadow: (t) => t.customShadows?.z1 || 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Section B · KYC Documents
            </Typography>
            <Chip
              size="small"
              label={`${uploadedCount} / ${KYC_DOCS.length} uploaded`}
              color={allDocsUploaded ? 'success' : 'default'}
              sx={{ fontWeight: 600 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Upload the four documents below. Backend upload API is pending — files are held in-memory for now
            so the flow is fully testable.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            Once backend wiring lands, each document POSTs to <code>/api/merchants/&#123;id&#125;/kyc-docs</code>,
            and the KYC Approvals workflow (owned by the <strong>MERCHANT_KYC_APPROVAL</strong> persona) picks
            up the review.
          </Alert>

          <Grid container spacing={1.5}>
            {KYC_DOCS.map((doc) => {
              const file = docs[doc.id];
              return (
                <Grid item xs={12} md={6} key={doc.id}>
                  <Box
                    sx={{
                      border: 1,
                      borderStyle: file ? 'solid' : 'dashed',
                      borderColor: file ? 'success.main' : 'divider',
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      bgcolor: file ? 'rgba(21,128,61,0.04)' : 'background.paper',
                    }}
                  >
                    {file ? (
                      <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 28 }} />
                    ) : (
                      <UploadFileOutlinedIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {doc.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {file ? file.name : doc.hint}
                      </Typography>
                    </Box>
                    {file ? (
                      <IconButton size="small" onClick={handleClearFile(doc.id)} aria-label="Remove file">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        sx={{ flexShrink: 0 }}
                      >
                        Choose file
                        <input
                          hidden
                          type="file"
                          accept="application/pdf,image/*"
                          onChange={handlePickFile(doc.id)}
                        />
                      </Button>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Card>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Continue CTA ── */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            On continue: signup data is saved (reusing the Pricing Wizard slice), KYC docs are staged for upload,
            and you&apos;re moved to <strong>Step 2 · Pricing Setup</strong>.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={handleSaveAndContinue}
            disabled={!allDocsUploaded}
            sx={{ minWidth: 280 }}
          >
            Save & Continue to Step 2 · Pricing
          </Button>
        </Stack>
      </Box>
    </>
  );
}
